/**
 * Persistent, user-scoped success alert shown at the top of
 * /account/claims and /account/subscription.
 *
 * Design:
 * - Triggered by actions that affect the subscription (Reactivate,
 *   Keep Current Plan). The message survives reloads and logout/login
 *   so the user keeps seeing it until they dismiss with ×.
 * - Stored in localStorage keyed by user id so one user's banner
 *   doesn't bleed to another user on the same browser.
 * - Both pages read the same key, so dismissing on one page also
 *   dismisses on the other.
 *
 * Shape stored:  `{ message: string }`
 */

const prefix = "sub-action-alert-";

const keyFor = (userId: string | null | undefined) => (userId ? `${prefix}${userId}` : null);

export function readSubscriptionAlert(userId: string | null | undefined): string | null {
  const key = keyFor(userId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.message === "string" ? parsed.message : null;
  } catch {
    return null;
  }
}

export function writeSubscriptionAlert(userId: string | null | undefined, message: string): void {
  const key = keyFor(userId);
  if (!key || typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify({ message }));
}

export function clearSubscriptionAlert(userId: string | null | undefined): void {
  const key = keyFor(userId);
  if (!key || typeof window === "undefined") return;
  localStorage.removeItem(key);
}
